import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';

type LibraryChatUnavailableProps = {
  onRetry?: () => void;
};

/**
 * Shown when Research cannot open: the flag lets the reader in but the
 * backend ability does not, or the account needed to decide that failed to
 * load. The two read differently because only one of them is worth retrying.
 */
export function LibraryChatUnavailable({ onRetry }: LibraryChatUnavailableProps) {
  const router = useRouter();

  const { top, bottom } = useSafeAreaInsets();

  const isLoadFailure = onRetry != null;

  return (
    <VStack
      className="flex-1 items-center justify-center gap-4 bg-background-0 px-6"
      style={{ paddingTop: top, paddingBottom: bottom }}>
      <Heading size="lg" className="text-center text-typography-900">
        {isLoadFailure ? 'Research couldn’t load' : 'Research isn’t available on your account yet'}
      </Heading>

      <Text className="max-w-md text-center text-base text-typography-600">
        {isLoadFailure
          ? 'Check your connection and try again.'
          : 'You can still search the Inquiry Library by keyword.'}
      </Text>

      <HStack className="flex-wrap justify-center gap-3">
        {isLoadFailure ? (
          <Button onPress={onRetry}>
            <ButtonText>Try again</ButtonText>
          </Button>
        ) : null}

        <Button
          variant={isLoadFailure ? 'outline' : 'solid'}
          action={isLoadFailure ? 'secondary' : 'primary'}
          onPress={() => router.replace(ROUTES.COMMUNITY)}>
          <ButtonText>Go to the Inquiry Library</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
